import { useState, useEffect } from 'react';
import AddScheduleItemModal from '../modals/AddScheduleItemModal';
import EditScheduleItemModal from '../modals/EditScheduleItemModal';
import { getSchedule, upsertScheduleItem, deleteScheduleItem, syncMasterSchedule } from '../../utils/api';
import {
  formatTime, buildTags, groupByDay, isDateHeaderRow, isSL, isMealItem, isWindowedMeal, TagPill,
} from '../../utils/scheduleFormat';

export default function AdminSchedule({ showToast }) {
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [slOnly, setSlOnly] = useState(false);
  const [mealsOnly, setMealsOnly] = useState(false);
  const [syncing, setSyncing] = useState(false);

  function loadSchedule() {
    setLoading(true);
    getSchedule()
      .then(data => setSchedule(data || []))
      .catch(err => console.error('Failed to load schedule:', err))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadSchedule(); }, []);

  // Pull the latest from the master schedule spreadsheet (configured in Settings).
  async function handleSyncMaster() {
    setSyncing(true);
    try {
      const res = await syncMasterSchedule();
      showToast(res?.report || 'Master schedule synced.');
      loadSchedule();
    } catch (err) {
      console.error('Sync failed:', err);
      showToast('Sync failed — check the spreadsheet link in Settings and account access.');
    } finally {
      setSyncing(false);
    }
  }

  // Called by EditScheduleItemModal with the full updated item object
  async function handleScheduleItemUpdated(updated) {
    const prev = schedule;
    setSchedule(s => s.map(item => item.id === updated.id ? { ...item, ...updated } : item));
    try {
      await upsertScheduleItem(updated);
      showToast('Schedule item saved.');
    } catch (err) {
      setSchedule(prev);
      showToast('Error saving item.');
      console.error(err);
    }
  }

  // Called by EditScheduleItemModal with the deleted item's id
  async function handleScheduleItemDeleted(id) {
    const prev = schedule;
    setSchedule(s => s.filter(item => item.id !== id));
    try {
      await deleteScheduleItem(id);
      showToast('Item deleted.');
    } catch (err) {
      setSchedule(prev);
      showToast('Error deleting item.');
      console.error(err);
    }
  }

  // Called by AddScheduleItemModal — creates need server IDs, so reload
  function handleScheduleItemAdded() {
    loadSchedule();
  }

  // Meal windows are hard-coded conference-wide (Breakfast 8–10:30, Lunch
  // 12–2:30, Dinner 4–7) — no admin setup; teams pick their time inside them.

  // Drop day/date divider rows before counting or displaying anything.
  const cleanSchedule = schedule.filter(item => !isDateHeaderRow(item));
  const slCount = cleanSchedule.filter(isSL).length;
  const mealCount = cleanSchedule.filter(isMealItem).length;
  let visibleSchedule = cleanSchedule;
  if (slOnly) visibleSchedule = visibleSchedule.filter(isSL);
  if (mealsOnly) visibleSchedule = visibleSchedule.filter(isMealItem);
  const grouped = groupByDay(visibleSchedule);

  return (
    <>
      <div className="page-content">
        <div className="page-title-row">
          <div>
            <div className="page-title">Master Schedule</div>
            <div className="page-sub">Conference-wide events · edited by Admin only</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              className={`filter-btn${slOnly ? ' active' : ''}`}
              onClick={() => setSlOnly(v => !v)}
              title="Show only items with the SL? box checked"
            >
              SL only{slCount ? ` (${slCount})` : ''}
            </button>
            <button
              className={`filter-btn${mealsOnly ? ' active' : ''}`}
              onClick={() => setMealsOnly(v => !v)}
              title="Show only meals"
            >
              Meals only{mealCount ? ` (${mealCount})` : ''}
            </button>
            <button
              className="btn"
              onClick={handleSyncMaster}
              disabled={syncing}
              title="Pull the latest from the master schedule spreadsheet (set in Settings → Conference Info)"
            >
              {syncing ? 'Syncing…' : '↻ Sync from Spreadsheet'}
            </button>
            <button className="btn btn-dark" onClick={() => setAddOpen(true)}>+ Add Item</button>
          </div>
        </div>

        {loading ? (
          <div className="panel" style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>
            Loading schedule…
          </div>
        ) : grouped.length === 0 ? (
          <div className="panel" style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>
            {slOnly || mealsOnly
              ? 'No items match the current filter. Turn off the filters to see the full schedule.'
              : <>No schedule items yet. Click <strong style={{ fontFamily: 'var(--font-h)', color: 'var(--black)' }}>+ Add Item</strong> to add the first item.</>}
          </div>
        ) : (
          grouped.map(group => (
            <div key={group.day}>
              <div className="section-header">{group.day}</div>
              <div className="panel" style={{ marginBottom: 24 }}>
                {group.items.map((item, i) => {
                  const tags = buildTags(item);
                  return (
                    <div className="schedule-item" key={item.id || i}>
                      <div className="schedule-time">
                        {formatTime(item.start)}
                        {item.end && (
                          <div style={{ fontFamily: 'var(--font-b)', fontSize: 10, fontWeight: 300, color: 'var(--muted)', marginTop: 2 }}>
                            &ndash;{formatTime(item.end)}
                          </div>
                        )}
                      </div>
                      <div className="schedule-detail">
                        <div className="schedule-name">{item.activity}</div>
                        <div className="schedule-loc">{item.location}</div>
                        <div className="schedule-tags">
                          {tags.map((tag, ti) => (
                            <TagPill key={ti} label={tag.label} cls={tag.cls} />
                          ))}
                          {isWindowedMeal(item) && (
                            <span
                              className="sc-pill"
                              style={{ background: 'var(--off)', color: 'var(--muted)', border: '1px solid var(--border)' }}
                              title="Fixed conference-wide window — each team picks its own time inside it"
                            >
                              Window {formatTime(item.mealWindowStart)}–{formatTime(item.mealWindowEnd)} · Teams pick their time
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                        <button
                          className="btn btn-sm"
                          onClick={() => { setEditItem(item); setEditOpen(true); }}
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      <AddScheduleItemModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSave={handleScheduleItemAdded}
        showToast={showToast}
      />
      <EditScheduleItemModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        item={editItem}
        onSave={handleScheduleItemUpdated}
        onDelete={handleScheduleItemDeleted}
        showToast={showToast}
      />
    </>
  );
}
